import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';

const VIRTUAL_ROW_HEIGHT = 56;
const VIRTUAL_ROW_MIN_HEIGHT = 52;
const VIRTUAL_THRESHOLD = 80;
const VIRTUAL_OVERSCAN = 6;

/**
 * Sortable, scannable table. Virtualizes body rows when row count exceeds VIRTUAL_THRESHOLD.
 */
export default function DataTable({
  columns,
  rows,
  rowKey,
  onRowClick,
  initialSort,
  emptyState = null,
  stickyHeader = false,
  className = '',
  bare = false,
  pageSize = null,
  page = 1,
  onSortChange,
}) {
  const [sort, setSort] = useState(initialSort || null);
  const scrollRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(480);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const getter = col.accessor || ((r) => r[sort.key]);
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getter(a);
      const bv = getter(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [rows, sort, columns]);

  const pageCount = pageSize ? Math.max(1, Math.ceil(sortedRows.length / pageSize)) : 1;
  const safePage = pageSize ? Math.min(Math.max(1, page), pageCount) : 1;

  const displayRows = useMemo(() => {
    if (!pageSize) return sortedRows;
    const start = (safePage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, pageSize, safePage]);

  const useVirtual = displayRows.length > VIRTUAL_THRESHOLD;

  const { sliceStart, sliceEnd, paddingTop, paddingBottom } = useMemo(() => {
    if (!useVirtual) {
      return { sliceStart: 0, sliceEnd: displayRows.length, paddingTop: 0, paddingBottom: 0 };
    }
    const visibleCount = Math.ceil(viewportHeight / VIRTUAL_ROW_HEIGHT) + VIRTUAL_OVERSCAN * 2;
    const start = Math.max(0, Math.floor(scrollTop / VIRTUAL_ROW_HEIGHT) - VIRTUAL_OVERSCAN);
    const end = Math.min(displayRows.length, start + visibleCount);
    return {
      sliceStart: start,
      sliceEnd: end,
      paddingTop: start * VIRTUAL_ROW_HEIGHT,
      paddingBottom: Math.max(0, (displayRows.length - end) * VIRTUAL_ROW_HEIGHT),
    };
  }, [useVirtual, displayRows.length, scrollTop, viewportHeight]);

  const visibleRows = useVirtual ? displayRows.slice(sliceStart, sliceEnd) : displayRows;

  const onScroll = useCallback((e) => {
    setScrollTop(e.currentTarget.scrollTop);
  }, []);

  useEffect(() => {
    if (!useVirtual || !scrollRef.current) return undefined;
    const el = scrollRef.current;
    const ro = new ResizeObserver(() => {
      setViewportHeight(el.clientHeight || 480);
    });
    ro.observe(el);
    setViewportHeight(el.clientHeight || 480);
    return () => ro.disconnect();
  }, [useVirtual]);

  const toggleSort = (col) => {
    if (col.sortable === false) return;
    setSort((prev) => {
      let next;
      if (!prev || prev.key !== col.key) next = { key: col.key, dir: 'asc' };
      else if (prev.dir === 'asc') next = { key: col.key, dir: 'desc' };
      else next = null;
      onSortChange?.(next);
      return next;
    });
  };

  const wrapClass = bare ? '' : 'app-table-wrap';

  if (!rows || rows.length === 0) {
    return (
      <div className={`${wrapClass} ${className}`.trim()}>
        <div className="p-10 flex items-center justify-center">{emptyState}</div>
      </div>
    );
  }

  return (
    <div className={`${wrapClass} ${className}`.trim()}>
      <div
        ref={useVirtual ? scrollRef : undefined}
        onScroll={useVirtual ? onScroll : undefined}
        className="overflow-x-auto"
        style={useVirtual ? { maxHeight: 'min(70vh, 640px)', overflowY: 'auto' } : undefined}
      >
        <table className="app-table w-full border-collapse text-[13px] min-w-[640px] md:min-w-0">
          <thead
            className={stickyHeader ? 'sticky top-0 z-[1]' : undefined}
            style={stickyHeader ? { background: 'var(--bg-s1)' } : undefined}
          >
            <tr>
              {columns.map((col) => {
                const active = sort && sort.key === col.key;
                const align =
                  col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                return (
                  <th
                    key={col.key}
                    className={`${align} select-none`}
                    style={{
                      width: col.width,
                      cursor: col.sortable === false ? 'default' : 'pointer',
                    }}
                    onClick={() => toggleSort(col)}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {col.label}
                      {col.sortable !== false && (
                        <span
                          className="inline-block"
                          style={{
                            fontSize: 9,
                            color: active ? 'var(--text-primary)' : 'var(--text-subtle)',
                          }}
                        >
                          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {useVirtual && paddingTop > 0 ? (
              <tr aria-hidden="true">
                <td colSpan={columns.length} style={{ height: paddingTop, padding: 0, border: 'none' }} />
              </tr>
            ) : null}
            {visibleRows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                style={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  minHeight: useVirtual ? VIRTUAL_ROW_MIN_HEIGHT : undefined,
                  height: useVirtual ? 'auto' : undefined,
                }}
              >
                {columns.map((col) => {
                  const align =
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                  const numeric = col.numeric ? 'app-numeric' : '';
                  return (
                    <td
                      key={col.key}
                      className={`${align} ${numeric} ${numeric ? 'app-numeric--nowrap' : 'app-readable'}`.trim()}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  );
                })}
              </tr>
            ))}
            {useVirtual && paddingBottom > 0 ? (
              <tr aria-hidden="true">
                <td colSpan={columns.length} style={{ height: paddingBottom, padding: 0, border: 'none' }} />
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}
